// src/ui/QRLogin.jsx — the signed-out landing: two full screens on one scroll.
//
//   pane 1  scan the QR code with the phone (what this screen used to be, entire)
//   pane 2  sign in with an email and password, for when the phone isn't the answer
//
// 🟥 THE MESH IS FIXED, NOT PER-PANE. Six slowly drifting blobs that blend into each
//    other are the background of the whole document; painting a copy inside each pane
//    would make the colour visibly jump at the seam as you scroll past it.
//
// The panes snap, but on `proximity` rather than `mandatory` — mandatory snapping fights
// a trackpad flick and traps a keyboard user mid-pane on a short window.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Lock, ShieldLock, SyncArrows, CallShield,
  Keyboard, Laptop, ShieldCheck, BellRing, ArrowDown, ArrowUp,
} from './icons';
import BrandLottie from './BrandLottie';
import LoginCard from './LoginCard';
import { createPairingSession, watchPairingSession } from '../services/pairing';
import { HOME, navigate } from '../services/siteRoute';
import './pairing.css';

const STATUS = {
  pending: { text: 'Waiting to scan…', linked: false },
  scanned: { text: 'Scanned — confirm on your phone', linked: false },
  authenticating: { text: 'Linking this device…', linked: false },
  linked: { text: 'Linked! Opening your chats…', linked: true },
  error: { text: 'Link failed — refreshing code…', linked: false },
};

// 🟢 One icon language: the same stroke weight and geometry as the rest of the app's
//    set, instead of two line icons and two emoji sitting next to each other.
const FEATURES = [
  {
    Icon: ShieldLock,
    title: 'End-to-end encrypted',
    sub: 'This browser gets a key of its own. Nobody in between can read a word of it.',
  },
  {
    Icon: SyncArrows,
    title: 'Instantly in sync',
    sub: 'Messages, receipts, unread counts and pins stay identical on both screens.',
  },
  {
    Icon: CallShield,
    title: 'Encrypted voice & video',
    sub: 'Calls on Allo carry the same protection your messages do, end to end.',
  },
];

/* 🟥 COPY THAT CONTRADICTS THE DEVICE READING IT IS THE LOUDEST KIND OF "NOT
      MOBILE". Every line below assumes a keyboard and a phone in your pocket —
      "no camera, no cable", "drag files into a conversation", "made for a full
      keyboard". Read on the phone itself they are simply untrue, and no amount
      of correct layout makes a screen feel built for you while it is describing
      somebody else's. So a phone gets its own three. */
const REASONS_MOBILE = [
  {
    Icon: ShieldCheck,
    title: 'Nobody gets in without your say-so',
    sub: 'A browser Allo hasn’t met waits for a device you trust to approve it — or for a six-digit code sent to your inbox.',
  },
  {
    Icon: BellRing,
    title: 'You hear about every login',
    sub: 'Each new sign-in arrives in your Note to Self with the device, the place on a map, and the time.',
  },
  {
    Icon: ShieldLock,
    title: 'Encrypted the whole way',
    sub: 'This browser gets a key of its own, and calls carry the same protection your messages do.',
  },
];

// The second pane says different things — it is about the account, not the pairing.
const REASONS = [
  {
    Icon: Laptop,
    title: 'The phone can stay in your pocket',
    sub: 'Sign in with the email and password you already use on Allo. No camera, no cable, no code to scan.',
  },
  {
    Icon: ShieldCheck,
    title: 'Nobody gets in without your say-so',
    sub: 'A browser Allo hasn’t met waits for a device you trust to approve it — or for a six-digit code sent to your inbox.',
  },
  {
    Icon: BellRing,
    title: 'You hear about every login',
    sub: 'Each new sign-in arrives in your Note to Self with the device, the place on a map, and the time.',
  },
  {
    Icon: Keyboard,
    title: 'Made for a full keyboard',
    sub: 'Type at speed, drag files straight into a conversation, and keep every shortcut your hands already know.',
  },
];

/**
 * Adds `.is-in` the first time an element scrolls into view, so the pane assembles
 * itself as you arrive instead of being fully painted before anyone has seen it.
 * One observer for the document; unobserve on first hit so nothing re-animates when you
 * scroll back up — a card that keeps re-playing reads as a glitch, not as polish.
 */
function useReveal(rootRef, deps = []) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const items = Array.from(root.querySelectorAll('[data-reveal]'));
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return undefined;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      });
    }, { root, threshold: 0.25 });
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * 🟥 A QR CODE IS USELESS ON THE DEVICE THAT IS SHOWING IT. Pane 1 asks you to
 *    point your phone's camera at this screen — which is exactly what you cannot
 *    do when this screen IS your phone. A phone visitor therefore landed on a
 *    dead end and had to discover, by scrolling, that there was a second pane
 *    with the form they actually needed.
 *
 *    So on a narrow screen the two panes swap: the sign-in form is what you
 *    land on, and the code moves to second, where it still serves the one case
 *    that makes sense there — reading it from a phone in order to link a
 *    computer sitting next to you. Nothing changes on a desktop.
 *
 * 🟢 THE SWAP IS CSS `order`, SO THE DOM NEVER MOVES. Re-ordering the JSX would
 *    remount both panes on every rotation — losing a half-typed password and
 *    restarting the pairing session. `order` re-paints; it does not re-mount.
 *    This flag exists only so the cue and the card's link know which way is
 *    which; the panes themselves are laid out by the stylesheet.
 */
function useNarrow(query = '(max-width: 820px)') {
  const [narrow, setNarrow] = useState(
    () => (typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia(query).matches : false),
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setNarrow(e.matches);
    setNarrow(mq.matches);
    // Safari < 14 has no addEventListener on MediaQueryList
    if (mq.addEventListener) { mq.addEventListener('change', on); return () => mq.removeEventListener('change', on); }
    mq.addListener(on); return () => mq.removeListener(on);
  }, [query]);
  return narrow;
}

export default function QRLogin() {
  const [qr, setQr] = useState('');
  const [status, setStatus] = useState('pending');
  const [beat, setBeat] = useState(0);        // bump to replay the mark
  const [atSecond, setAtSecond] = useState(false);
  /* 🟥 TWO THRESHOLDS, BECAUSE THEY ANSWER TWO DIFFERENT QUESTIONS.
     `narrow` (≤820) is "is there room for two columns" — it decides the order of
     the panes and which way the cue points, and a tablet answers it the same way
     a phone does.
     `phone` (≤600) is "can the device showing this code also scan it" — and there
     the tablet answers differently. An iPad in portrait is 768px wide, and
     scanning ITS screen with the phone in your hand is a perfectly good way to
     link it. Collapsing both questions into one breakpoint would take the code
     away from the one narrow device that can still use it. */
  const narrow = useNarrow('(max-width: 820px)');
  const phone = useNarrow('(max-width: 600px)');
  const unsubRef = useRef(null);
  const docRef = useRef(null);
  const loginRef = useRef(null);
  const scanRef = useRef(null);

  const start = useCallback(async () => {
    try {
      if (unsubRef.current) unsubRef.current();
      const { sessionId, qrPayload } = await createPairingSession();
      setQr(qrPayload); setStatus('pending');
      unsubRef.current = watchPairingSession(sessionId, {
        onStatus: (s) => { setStatus(s); if (s === 'error') setTimeout(start, 1500); },
      });
    } catch { setStatus('error'); setTimeout(start, 2000); }
  }, []);

  useEffect(() => { start(); return () => unsubRef.current && unsubRef.current(); }, [start]);
  useReveal(docRef, []);

  /**
   * One scroll listener drives two things:
   *   --sc     0→1 across the first screen, so the cue can fade and the panes can
   *            drift against each other as you travel between them
   *   atSecond which pane you're on, so the cue flips from "down" to "back"
   * rAF-throttled: the raw scroll event fires far faster than the compositor can use.
   *
   * 🟥 MEASURED AGAINST THE SECOND PANE, NOT AGAINST ONE VIEWPORT HEIGHT. This
   *    used to call it `scrollTop > half the window` — true only while both panes
   *    are exactly one screen tall. On a phone they are not: the hero, the four
   *    reasons and the card stack into something well over a screen, so a reader
   *    a third of the way down pane 1 was already being told they were on pane 2
   *    and handed a cue pointing back up. Asking the pane where it actually
   *    starts is right at any height.
   */
  useEffect(() => {
    const el = docRef.current;
    if (!el) return undefined;
    let raf = 0;
    const read = () => {
      raf = 0;
      const h = el.clientHeight || 1;
      el.style.setProperty('--sc', Math.max(0, Math.min(1, el.scrollTop / h)).toFixed(3));
      const second = narrow ? scanRef.current : loginRef.current;
      const at = second ? second.offsetTop - h * 0.5 : h * 0.5;
      setAtSecond(el.scrollTop >= at);
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(read); };
    read();
    el.addEventListener('scroll', onScroll, { passive: true });
    /* re-measure when the order flips, and when a rotation changes the heights */
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [narrow]);

  const goFirst = () => docRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  const goSecond = () => (narrow ? scanRef : loginRef).current
    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  /* the card's own "scan the code instead" link: down on a phone, up on a desktop */
  const goQr = narrow ? goSecond : goFirst;

  const st = STATUS[status] || STATUS.pending;

  return (
    <div className="pair-doc" ref={docRef}>
      {/* six slow blobs that blend into each other — see pairing.css */}
      <div className="mesh" aria-hidden="true">
        <span className="m1" /><span className="m2" /><span className="m3" />
        <span className="m4" /><span className="m5" /><span className="m6" />
      </div>
      <div className="grain" aria-hidden="true" />

      {/* ⭐ THE WAY BACK OUT.
          🟥 THIS SCREEN WAS A DEAD END. Signing out lands on /app with no user,
             which renders this document — and it has no header, no nav and no
             link of any kind, so the only route back to the site was editing the
             URL bar. Somebody who just logged out and wants to read the About
             page has no way to get there.

          🟢 FIXED, NOT IN A PANE. This is a two-pane scroll-snap document; a link
             placed inside pane 1 would scroll away the moment anyone moved to the
             sign-in pane, which is exactly where a person who is stuck would be.

          🟥 AND IT IS A REAL <a href="/">, not a button. Middle-click and
             "open in new tab" have to work on the one link that leaves the app,
             and a router that only listens for pushState gets that for free by
             preventing the default rather than replacing the element. */}
      <a
        className="pair-exit"
        href="/"
        onClick={(e) => { e.preventDefault(); navigate(HOME); }}
      >
        <span className="pair-exit-arrow" aria-hidden="true">‹</span>
        allloapp.com
      </a>
      {/* ⚖️ before anyone signs in: the Terms, the Privacy Policy, the cookies — fixed, like the exit */}
      <nav className="pair-legal" aria-label="Legal">
        <a href="/terms" target="_blank" rel="noopener">Terms</a>
        <a href="/privacy" target="_blank" rel="noopener">Privacy</a>
        <a href="/cookies" target="_blank" rel="noopener">Cookies</a>
      </nav>

      {/* ══════════ pane 1 — scan ══════════ */}
      <section className="pair-pane" id="pair-scan" ref={scanRef}>
        <div className="pair-wrap">
          {/* ── hero ── */}
          <div className="pair-hero">
            <button
              className="pair-mark"
              onClick={() => setBeat((b) => b + 1)}
              title="Play again"
              aria-label="Allo"
            >
              <BrandLottie key={beat} size={230} bare />
            </button>

            <h1 className="pair-hl">Your conversations,<br /><span className="grad">now on the big screen.</span></h1>
            <p className="pair-lead">
              Message from your computer with the same end-to-end encryption as your phone.
              Fast, private, and beautifully in sync.
            </p>

            <div className="pair-feats">
              {FEATURES.map(({ Icon, title, sub }) => (
                <div className="pair-feat" key={title}>
                  <div className="fi"><Icon width={19} height={19} /></div>
                  <div>
                    <div className="ft">{title}</div>
                    <div className="fs">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── QR card ── */}
          <div className="pair-card2">
            <h3>{phone ? 'Linking a computer?' : 'Link this device'}</h3>
            <div className="csub">
              {phone
                ? 'Open allloapp.com on the computer, then scan the code it shows'
                : 'Scan with AlloApp on your phone'}
            </div>

            <div className="qr-stage">
              <div className="qr-plate">
                {qr
                  ? <QRCodeSVG value={qr} size={210} level="M" bgColor="#ffffff" fgColor="#0b1220" />
                  : <div style={{ color: '#8a93a3' }}>Generating…</div>}
                <div className="qr-laser" />
              </div>
              <span className="qr-corner tl" /><span className="qr-corner tr" />
              <span className="qr-corner bl" /><span className="qr-corner br" />
            </div>

            <div className={`pair-status2 ${st.linked ? 'linked' : ''}`}>
              <span className="pulse-dot" />{st.text}
            </div>

            {/* 🟥 These steps used to describe a "Menu → Linked devices" screen that does
                not exist in AlloApp. The real scanner lives behind the SEARCH icon on the
                home screen — inside the "search by username" bar. Steps match the app. */}
            {/* 🟥 THE SENTENCE MUST LIVE IN ONE <span>, NOT LOOSE IN THE FLEX ROW.
                .pair-step2 is display:flex with gap:12px. In flex layout every child
                ELEMENT is its own flex item, and each run of bare text between elements
                becomes its own anonymous item too — so "…<b>AlloApp</b>…" was FOUR items
                (number, "Open", the bold word, "on your phone") with a 12px gap injected
                on both sides of the bold word, and align-items:center levelled each item
                by its own box so the bold word sat off the line of the words around it.
                Wrapping the sentence makes it two items — number + text — and the bold
                word goes back to being ordinary inline text inside a normal line box. */}
            {/* 🟥 THE STEPS HAVE TO POINT AT THE RIGHT SCREEN. On a phone the code
                above is hidden (see mobile.css) because it is the one code nobody
                here can scan — the camera and the display are the same device.
                What a phone visitor can do is walk a computer through it, so the
                steps describe that instead of telling them to point this phone at
                itself. */}
            <div className="pair-steps2">
              {phone ? (
                <>
                  <div className="pair-step2"><span className="n">1</span><span className="t">On the computer, open <b>allloapp.com</b></span></div>
                  <div className="pair-step2"><span className="n">2</span><span className="t">On this phone, open <b>Allo</b> and tap the <b>search</b> icon</span></div>
                  <div className="pair-step2"><span className="n">3</span><span className="t">Tap the <b>QR</b> icon in the search bar</span></div>
                  <div className="pair-step2"><span className="n">4</span><span className="t">Point this phone at the code on the computer</span></div>
                </>
              ) : (
                <>
                  <div className="pair-step2"><span className="n">1</span><span className="t">Open <b>AlloApp</b> on your phone</span></div>
                  <div className="pair-step2"><span className="n">2</span><span className="t">Tap the <b>search</b> icon at the top of your chats</span></div>
                  <div className="pair-step2"><span className="n">3</span><span className="t">Tap the <b>QR</b> icon in the search bar</span></div>
                  <div className="pair-step2"><span className="n">4</span><span className="t">Point your phone at this code</span></div>
                </>
              )}
            </div>

            <div className="enc-foot"><Lock width={12} height={12} /> Secured with end-to-end encryption</div>
          </div>
        </div>
      </section>

      {/* ══════════ pane 2 — sign in ══════════ */}
      <section className="pair-pane pane-login" id="pair-login" ref={loginRef}>
        <div className="pair-wrap login-wrap">
          <div className="pair-hero login-hero">
            <div className="lh-top" data-reveal>
              <span className="lh-mark"><BrandLottie size={92} bare /></span>
              {/* "No phone nearby?" is a question you can only be asked on a
                  computer — on a phone it is a joke at the reader's expense */}
              <span className="lh-kicker">{narrow ? 'Welcome back' : 'No phone nearby?'}</span>
            </div>

            {/* one line — the break was splitting a four-word sentence in half */}
            <h2 className="pair-hl small" data-reveal>
              Sign in the <span className="grad">ordinary way.</span>
            </h2>

            <div className="reasons">
              {(narrow ? REASONS_MOBILE : REASONS).map(({ Icon, title, sub }, i) => (
                <div className="reason" key={title} data-reveal style={{ '--i': i }}>
                  <span className="rz-rail" aria-hidden="true" />
                  <span className="rz-ico"><Icon width={19} height={19} /></span>
                  <div className="rz-body">
                    <div className="rz-t">{title}</div>
                    <div className="rz-s">{sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="login-col" data-reveal>
            <LoginCard onScrollUp={goQr} narrow={phone} />
          </div>
        </div>
      </section>

      {/* ══════════ the cue ══════════
          One control, not two: it points down on the first screen and back up on the
          second, so there is never a dead arrow on screen. */}
      <button
        type="button"
        className={`scroll-cue ${atSecond ? 'up' : ''}`}
        onClick={atSecond ? goFirst : goSecond}
      >
        <span className="cue-txt">
          {narrow
            ? (atSecond ? 'Back to sign in' : (phone ? 'Linking a computer?' : 'Scan a QR code instead'))
            : (atSecond ? 'Back to the QR code' : 'Scroll to log in')}
        </span>
        <span className="cue-ring">
          {atSecond ? <ArrowUp width={26} height={26} /> : <ArrowDown width={26} height={26} />}
        </span>
      </button>
    </div>
  );
}
